import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Profile, ProfilePatch } from '../../types';
import { toNumberOrNull } from '../../utils/format';

interface Props {
  profile: Profile;
  saving: boolean;
  onSave: (patch: ProfilePatch) => Promise<void>;
}

type TextKey = Exclude<
  keyof Profile,
  | 'email'
  | 'years_experience'
  | 'preferred_roles'
  | 'preferred_locations'
  | 'preferred_tech_stack'
  | 'preferred_salary_min'
  | 'preferred_salary_max'
>;

interface FieldDef {
  key: TextKey;
  label: string;
  sm?: number;
  autoComplete?: string;
  type?: string;
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: 'Personal',
    fields: [
      { key: 'first_name', label: 'First name', sm: 4, autoComplete: 'given-name' },
      { key: 'last_name', label: 'Last name', sm: 4, autoComplete: 'family-name' },
      { key: 'preferred_name', label: 'Preferred name', sm: 4 },
      { key: 'phone_country_code', label: 'Phone country code', sm: 3 },
      { key: 'phone', label: 'Phone', sm: 9, autoComplete: 'tel', type: 'tel' },
    ],
  },
  {
    title: 'Address',
    fields: [
      { key: 'address_line1', label: 'Address line 1', sm: 12, autoComplete: 'address-line1' },
      { key: 'address_line2', label: 'Address line 2', sm: 12, autoComplete: 'address-line2' },
      { key: 'city', label: 'City', sm: 4, autoComplete: 'address-level2' },
      { key: 'region', label: 'State / Region', sm: 4, autoComplete: 'address-level1' },
      { key: 'postal_code', label: 'Postal code', sm: 4, autoComplete: 'postal-code' },
      { key: 'country', label: 'Country', sm: 12, autoComplete: 'country-name' },
    ],
  },
  {
    title: 'Links',
    fields: [
      { key: 'linkedin_url', label: 'LinkedIn URL', sm: 6, type: 'url' },
      { key: 'github_url', label: 'GitHub URL', sm: 6, type: 'url' },
      { key: 'portfolio_url', label: 'Portfolio URL', sm: 6, type: 'url' },
      { key: 'website_url', label: 'Website URL', sm: 6, type: 'url' },
    ],
  },
  {
    title: 'Employment',
    fields: [
      { key: 'current_employer', label: 'Current employer', sm: 6, autoComplete: 'organization' },
      { key: 'current_title', label: 'Current title', sm: 6, autoComplete: 'organization-title' },
    ],
  },
];

type FormState = Record<TextKey, string> & { years_experience: string };

function toForm(profile: Profile): FormState {
  const state = {} as FormState;
  SECTIONS.forEach((s) =>
    s.fields.forEach((f) => {
      state[f.key] = profile[f.key] ?? '';
    })
  );
  state.years_experience =
    profile.years_experience === null || profile.years_experience === undefined
      ? ''
      : String(profile.years_experience);
  return state;
}

const ProfileDetailsForm: React.FC<Props> = ({ profile, saving, onSave }) => {
  const [form, setForm] = useState<FormState>(() => toForm(profile));

  useEffect(() => {
    setForm(toForm(profile));
  }, [profile]);

  const initial = useMemo(() => toForm(profile), [profile]);
  const dirty = useMemo(
    () => (Object.keys(form) as (keyof FormState)[]).some((k) => form[k] !== initial[k]),
    [form, initial]
  );

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: ProfilePatch = {};
    (Object.keys(form) as (keyof FormState)[]).forEach((k) => {
      if (form[k] === initial[k]) return;
      if (k === 'years_experience') {
        patch.years_experience = toNumberOrNull(form.years_experience);
      } else {
        patch[k] = form[k].trim();
      }
    });
    await onSave(patch);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={4}>
        {SECTIONS.map((section) => (
          <Box key={section.title}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {section.title}
            </Typography>
            <Grid container spacing={2}>
              {section.title === 'Personal' && (
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    value={profile.email}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText="Email is managed from your account and cannot be changed here."
                    size="small"
                  />
                </Grid>
              )}
              {section.fields.map((f) => (
                <Grid item xs={12} sm={f.sm ?? 6} key={f.key}>
                  <TextField
                    label={f.label}
                    value={form[f.key]}
                    onChange={set(f.key)}
                    type={f.type ?? 'text'}
                    autoComplete={f.autoComplete}
                    fullWidth
                    size="small"
                    disabled={saving}
                  />
                </Grid>
              ))}
              {section.title === 'Employment' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Years of experience"
                    value={form.years_experience}
                    onChange={set('years_experience')}
                    type="number"
                    inputProps={{ min: 0, max: 80, step: 1 }}
                    fullWidth
                    size="small"
                    disabled={saving}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        ))}

        <Divider />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            disabled={!dirty || saving}
            onClick={() => setForm(initial)}
          >
            Reset
          </Button>
          <Button type="submit" variant="contained" disabled={!dirty || saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProfileDetailsForm;
